import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import PuntiPaymentSummary from '../EventDetail/PuntiPaymentSummary';
import { createRewardOrder } from '../../utils/rewardsApi';
import { getRewardImageUrl } from '../../utils/rewardMediaUrl';
import { CIL_POINTS_LABEL, formatCilPoints } from '../../utils/cilPoints';

const RewardRedeemDrawer = ({
  isOpen,
  onClose,
  reward,
  isAuthenticated,
  userPunti,
  onSuccess,
}) => {
  const toast = useToast();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const maxQty = reward?.stockQuantity || 0;
  const unitCost = Number(reward?.pointsCost) || 0;
  const totalPoints = unitCost * quantity;

  useEffect(() => {
    if (isOpen && reward) {
      setQuantity(1);
    }
    if (!isOpen) {
      onConfirmClose();
    }
  }, [isOpen, reward?.documentId, onConfirmClose]);

  const disabledReason = useMemo(() => {
    if (!reward) return 'Premio non disponibile';
    if (maxQty < 1) return 'Premio esaurito';
    if (quantity < 1) return 'Seleziona una quantità';
    if (quantity > maxQty) return `Massimo ${maxQty} disponibili`;
    return null;
  }, [reward, maxQty, quantity]);

  const canRedeem =
    isAuthenticated &&
    !disabledReason &&
    totalPoints > 0 &&
    (Number(userPunti) || 0) >= totalPoints;

  const handleRequestConfirm = () => {
    if (!canRedeem || !reward) return;
    onConfirmOpen();
  };

  const handleConfirmRedeem = async () => {
    if (!canRedeem || !reward) return;
    setSubmitting(true);
    try {
      const result = await createRewardOrder([
        { rewardId: reward.documentId, quantity },
      ]);
      onConfirmClose();
      toast({
        title: 'Riscatto completato',
        description: (
          <Box as="div" lineHeight="tall">
            <Text fontSize="sm" display="block" mb={1}>
              Hai speso{' '}
              <Text as="span" fontWeight="bold">
                {formatCilPoints(result.puntiSpesi)}
              </Text>
            </Text>
            <Text fontSize="sm" display="block" mb={1}>
              Saldo aggiornato:{' '}
              <Text as="span" fontWeight="bold">
                {formatCilPoints(result.saldoPuntiDopo)}
              </Text>
            </Text>
            <Text fontSize="sm" display="block">
              Ritiro in sede
            </Text>
            <Text
              fontSize="sm"
              fontWeight="semibold"
              display="block"
              mt={0.5}
              whiteSpace="nowrap"
            >
              Account → I tuoi premi
            </Text>
          </Box>
        ),
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
      onSuccess?.(result);
      onClose();
    } catch (err) {
      toast({
        title: 'Riscatto non riuscito',
        description: err.response?.data?.error || err.message || 'Errore',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!reward) return null;

  const img = getRewardImageUrl(reward.image);

  const CONFIRM_Z = 100010;

  return (
    <>
      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent borderTopRadius="2xl" maxH="90dvh">
          <DrawerCloseButton top={3} />
          <DrawerHeader pb={2}>
            <Heading size="md" pr={8}>
              Riscatta premio
            </Heading>
            <Text fontSize="sm" color="gray.600" fontWeight="normal" mt={1}>
              {reward.name}
            </Text>
          </DrawerHeader>

          <DrawerBody pt={0}>
            {!isAuthenticated ? (
              <Box py={4}>
                <Text mb={4} color="gray.700">
                  Accedi per riscattare questo premio con i tuoi{' '}
                  {CIL_POINTS_LABEL}.
                </Text>
                <Button
                  as={RouterLink}
                  to="/login"
                  colorScheme="purple"
                  size="lg"
                  w="full"
                  onClick={onClose}
                >
                  Accedi o registrati
                </Button>
              </Box>
            ) : (
              <Flex direction="column" gap={4}>
                {img && (
                  <Box
                    w="120px"
                    h="160px"
                    maxW="40vw"
                    mx="auto"
                    flexShrink={0}
                    borderRadius="lg"
                    overflow="hidden"
                    bg="gray.100"
                  >
                    <Image
                      src={img}
                      alt={reward.name}
                      w="full"
                      h="full"
                      objectFit="cover"
                    />
                  </Box>
                )}

                <SimpleGrid columns={2} spacing={3}>
                  <Box
                    px={3}
                    py={3}
                    borderRadius="lg"
                    bg="purple.50"
                    borderWidth="1px"
                    borderColor="purple.100"
                    textAlign="center"
                  >
                    <Text
                      fontSize="xs"
                      color="purple.600"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      letterSpacing="0.04em"
                      mb={1}
                    >
                      Disponibili
                    </Text>
                    <Text
                      fontSize="xl"
                      fontWeight="bold"
                      color="purple.800"
                      lineHeight="1"
                    >
                      {maxQty}
                    </Text>
                  </Box>
                  <Box
                    px={3}
                    py={3}
                    borderRadius="lg"
                    bg="gray.50"
                    borderWidth="1px"
                    borderColor="gray.200"
                    textAlign="center"
                  >
                    <Text
                      fontSize="xs"
                      color="gray.500"
                      fontWeight="semibold"
                      textTransform="uppercase"
                      letterSpacing="0.04em"
                      mb={1}
                    >
                      Costo unitario
                    </Text>
                    <Text
                      fontSize="xl"
                      fontWeight="bold"
                      color="gray.800"
                      lineHeight="1"
                    >
                      {unitCost}
                      <Text
                        as="span"
                        fontSize="sm"
                        fontWeight="semibold"
                        ml={1}
                      >
                        pt
                      </Text>
                    </Text>
                  </Box>
                </SimpleGrid>

                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    mb={3}
                    color="gray.700"
                  >
                    Quantità
                  </Text>
                  <HStack spacing={4} justify="center">
                    <IconButton
                      aria-label="Diminuisci quantità"
                      icon={
                        <Text fontSize="2xl" lineHeight={1} fontWeight="bold">
                          −
                        </Text>
                      }
                      size="lg"
                      variant="outline"
                      colorScheme="purple"
                      borderRadius="full"
                      isDisabled={quantity <= 1}
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    />
                    <Text
                      fontSize="2xl"
                      fontWeight="bold"
                      minW="2.5rem"
                      textAlign="center"
                      aria-live="polite"
                    >
                      {quantity}
                    </Text>
                    <IconButton
                      aria-label="Aumenta quantità"
                      icon={
                        <Text fontSize="2xl" lineHeight={1} fontWeight="bold">
                          +
                        </Text>
                      }
                      size="lg"
                      variant="outline"
                      colorScheme="purple"
                      borderRadius="full"
                      isDisabled={quantity >= maxQty}
                      onClick={() =>
                        setQuantity((q) => Math.min(maxQty, q + 1))
                      }
                    />
                  </HStack>
                </Box>

                <PuntiPaymentSummary saldo={userPunti} costo={totalPoints} />

                {disabledReason && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    {disabledReason}
                  </Alert>
                )}
              </Flex>
            )}
          </DrawerBody>

          {isAuthenticated && (
            <DrawerFooter flexDirection="column" gap={2} borderTopWidth="1px">
              <Button
                w="full"
                size="lg"
                colorScheme="purple"
                isDisabled={!canRedeem || submitting}
                onClick={handleRequestConfirm}
              >
                Conferma riscatto ({formatCilPoints(totalPoints)})
              </Button>
              <Button
                w="full"
                variant="ghost"
                onClick={onClose}
                isDisabled={submitting}
              >
                Annulla
              </Button>
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>

      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={undefined}
        onClose={onConfirmClose}
        isCentered
        blockScrollOnMount={false}
      >
        <AlertDialogOverlay zIndex={CONFIRM_Z} bg="blackAlpha.600">
          <AlertDialogContent zIndex={CONFIRM_Z + 1} mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confermi il riscatto?
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={3}>
                Stai per prenotare <strong>{reward.name}</strong>
                {quantity > 1 ? ` × ${quantity}` : ''} per{' '}
                <strong>{formatCilPoints(totalPoints)}</strong>.
              </Text>
              <Text color="gray.600" fontSize="sm" lineHeight="tall">
                I punti verranno scalati dal tuo saldo. Il premio andrà ritirato
                in sede; trovi lo stato in:
              </Text>
              <Text
                mt={2}
                fontSize="sm"
                fontWeight="semibold"
                color="purple.700"
                whiteSpace="nowrap"
              >
                Account → I tuoi premi
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter flexDirection="column" gap={2}>
              <Button
                w="full"
                colorScheme="purple"
                onClick={handleConfirmRedeem}
                isLoading={submitting}
                loadingText="Riscatto in corso..."
              >
                Sì, riscatta ora
              </Button>
              <Button
                w="full"
                variant="ghost"
                onClick={onConfirmClose}
                isDisabled={submitting}
              >
                Torna indietro
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default RewardRedeemDrawer;
